import type { FixturesData } from '@/lib/network';
import type { PeriodLabels } from '@/lib/periodLabels';

interface FixturePeriodOption {
  id: string;
  label: string;
  value: string;
}

/**
 * World Cup matchdays 4-8 are knockout rounds and are named as such on the
 * fixture grid and in its period filter. This map is only consulted when the
 * active competition uses the matchday axis.
 */
const KNOCKOUT_ROUND_LABELS: Record<string, string> = {
  '4': 'R32',
  '5': 'R16',
  '6': 'QF',
  '7': 'SF',
  '8': 'F',
};

function getFixturePeriodNumber(periodKey: string): number | null {
  const numericPart = periodKey.replace(/^(MD|GW)/, '');
  const number = Number(numericPart);
  return Number.isFinite(number) ? number : null;
}

export function normaliseFixturePeriodValue(periodKey: string): string {
  return periodKey.replace(/^(MD|GW)/, '') || periodKey;
}

function sortFixturePeriodKeys(keys: string[]): string[] {
  return [...new Set(keys)].sort((left, right) => {
    const leftNumber = getFixturePeriodNumber(left);
    const rightNumber = getFixturePeriodNumber(right);

    if (leftNumber !== null && rightNumber !== null) {
      return leftNumber - rightNumber;
    }

    return left.localeCompare(right);
  });
}

/**
 * Prefer the explicit period list returned by the API. Falling back to keys
 * discovered on team fixtures keeps the filter usable with older responses.
 *
 * This is the complete period list and stays unfiltered so the period dropdown
 * can offer every period, played ones included. Use
 * `excludePlayedFixturePeriodKeys` where played periods should be hidden.
 */
export function getFixturePeriodKeys(fixturesData: FixturesData | undefined): string[] {
  const responsePeriodKeys =
    fixturesData?.matchDays && fixturesData.matchDays.length > 0
      ? fixturesData.matchDays
      : fixturesData?.gameWeeks;

  if (responsePeriodKeys && responsePeriodKeys.length > 0) {
    return sortFixturePeriodKeys(responsePeriodKeys);
  }

  const discoveredKeys = new Set<string>();
  for (const team of fixturesData?.teams ?? []) {
    Object.keys(team.fixtures).forEach(key => discoveredKeys.add(key));
  }

  return sortFixturePeriodKeys([...discoveredKeys]);
}

/**
 * Period keys whose every known fixture has status `played`.
 *
 * Status lives on individual fixtures rather than on the period, so team
 * fixtures are tallied per period key: a period only counts as played out once
 * it is known to have fixtures and none of them are still to come. The tally
 * spans all teams, so a period one team has finished stays open while another
 * team still has it to play. Period keys with no fixture data at all (the API's
 * explicit list often arrives without any teams) are unknown, not played.
 */
function getPlayedOutPeriodKeys(fixturesData: FixturesData | undefined): Set<string> {
  const playedOutKeys = new Set<string>();
  const periodsWithUnplayedFixtures = new Set<string>();

  for (const team of fixturesData?.teams ?? []) {
    for (const [periodKey, fixtures] of Object.entries(team.fixtures)) {
      for (const fixture of fixtures) {
        if (fixture.status === 'played') {
          playedOutKeys.add(periodKey);
        } else {
          periodsWithUnplayedFixtures.add(periodKey);
        }
      }
    }
  }

  for (const periodKey of periodsWithUnplayedFixtures) {
    playedOutKeys.delete(periodKey);
  }

  return playedOutKeys;
}

/**
 * Drop the periods that have been played out, preserving the order of
 * `periodKeys`. Kept separate from `getFixturePeriodKeys` so the dropdown can
 * still list every period while the grid shows only what is left to play.
 */
export function excludePlayedFixturePeriodKeys(
  periodKeys: string[],
  fixturesData: FixturesData | undefined
): string[] {
  const playedOutKeys = getPlayedOutPeriodKeys(fixturesData);

  if (playedOutKeys.size === 0) {
    return periodKeys;
  }

  return periodKeys.filter(periodKey => !playedOutKeys.has(periodKey));
}

export function getFixturePeriodLabel(periodKey: string, labels: PeriodLabels): string {
  const periodValue = normaliseFixturePeriodValue(periodKey);

  if (!labels.isGameweek) {
    const knockoutLabel = KNOCKOUT_ROUND_LABELS[periodValue];
    if (knockoutLabel) {
      return knockoutLabel;
    }
  }

  return `${labels.short} ${periodValue}`;
}

function getFixturePeriodFilterLabel(periodKey: string, labels: PeriodLabels): string {
  const periodValue = normaliseFixturePeriodValue(periodKey);

  if (!labels.isGameweek) {
    const knockoutLabel = KNOCKOUT_ROUND_LABELS[periodValue];
    if (knockoutLabel) {
      return knockoutLabel;
    }
  }

  return `${labels.long} ${periodValue}`;
}

export function buildFixturePeriodOptions(
  periodKeys: string[],
  labels: PeriodLabels
): FixturePeriodOption[] {
  return [
    { id: 'all', label: labels.all, value: 'all' },
    ...periodKeys.map(periodKey => ({
      id: `period-${periodKey}`,
      label: getFixturePeriodFilterLabel(periodKey, labels),
      value: normaliseFixturePeriodValue(periodKey),
    })),
  ];
}

export function filterFixturePeriodKeys(periodKeys: string[], selectedPeriods: string[]): string[] {
  if (selectedPeriods.includes('all')) {
    return periodKeys;
  }

  return periodKeys.filter(periodKey =>
    selectedPeriods.includes(normaliseFixturePeriodValue(periodKey))
  );
}
